import type { FormProps } from 'antd';
import { Button, Col, Form, Input, InputNumber, message } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Organization } from '../../Types/interfaces';
import { PostOrganization } from '../../helper/apiHelper';
import { useCurrentUser } from '../../hooks/useCurrentUser';

type FieldType = {
  universityOrganizationName: string;
  name: string;
  phoneNumber: string;
  orgDescription?: string;
  bachelorStudents: number;
  masterStudents: number;
};

const onFinishFailed: FormProps<FieldType>['onFinishFailed'] = (errorInfo) => {
  console.log('Failed:', errorInfo);
};
export const UniversityOrganizationRegistration = (): React.ReactElement => {
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const [messageApi, contextHolder] = message.useMessage();

  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    const newOrg: Organization = {
      representativeId: loggedInUser.id,
      representativeName: values.name,
      name: values.universityOrganizationName,
      description: values.orgDescription ?? '',
      city: '',
      county: '',
      street: '',
      streetNumber: '',
      phoneNumber: '',
      email: '',
      fax: '',
      fiscalCode: '',
      fiscalAccount: '',
      iban: '',
      isRomanian: true,
      bachelorStudents: values.bachelorStudents ?? 0,
      masterStudents: values.masterStudents ?? 0,
      language: 0,
      studyYearId: 0,
    };

    try {
      await PostOrganization(newOrg);
      messageApi.success(t('orgRegComp.regSuccess'));
    } catch {
      messageApi.error(t('orgRegComp.regError'));
    }
  };
  return (
    <div style={{ width: '100%' }}>
      {contextHolder}
      <Form
        name="basic"
        labelCol={{ span: 12 }}
        wrapperCol={{ span: 16 }}
        style={{ maxWidth: 600 }}
        initialValues={{ remember: true }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <Col>
          <Form.Item<FieldType>
            label={t('univOrganizationRegistration.organizationName')}
            name="universityOrganizationName"
            extra={t(
              'univOrganizationRegistration.descriptions.organizationName'
            )}
            rules={[
              {
                required: true,
                message: t('univOrganizationRegistration.validator.orgName'),
              },
            ]}
          >
            <Input
              placeholder={t(
                'univOrganizationRegistration.placeholders.organizationName'
              )}
            />
          </Form.Item>

          <Form.Item<FieldType>
            label={t('univOrganizationRegistration.name')}
            name="name"
            extra={t('univOrganizationRegistration.descriptions.name')}
            rules={[
              {
                required: true,
                message: t('univOrganizationRegistration.validator.name'),
              },
            ]}
          >
            <Input
              placeholder={t('univOrganizationRegistration.placeholders.name')}
            />
          </Form.Item>

          <Form.Item<FieldType>
            label={t('univOrganizationRegistration.phoneNumber')}
            name="phoneNumber"
            extra={t('univOrganizationRegistration.descriptions.phoneNumber')}
            rules={[
              {
                required: true,
                message: t(
                  'univOrganizationRegistration.validator.phoneNumber'
                ),
              },
            ]}
          >
            <Input
              placeholder={t(
                'univOrganizationRegistration.placeholders.phoneNumber'
              )}
            />
          </Form.Item>

          <Form.Item<FieldType>
            label={t('uniForm.sessionComp.description')}
            name="orgDescription"
            extra={t('univOrganizationRegistration.descriptions.description')}
          >
            <Input.TextArea
              placeholder={t(
                'univOrganizationRegistration.placeholders.description'
              )}
            />
          </Form.Item>

          <Form.Item
            name="bachelorStudents"
            label={t('orgRegComp.bachelorStudents')}
            tooltip={t('orgRegComp.studentCountHint')}
            rules={[
              {
                required: true,
                message: t('orgRegComp.bachelorStudentsNeeded'),
              },
              {
                type: 'number',
                min: 0,
                message: t('orgRegComp.nonNegativeNumber'),
              },
            ]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder={t('orgRegComp.bachelorStudents')}
            />
          </Form.Item>

          <Form.Item
            name="masterStudents"
            label={t('orgRegComp.masterStudents')}
            tooltip={t('orgRegComp.studentCountHint')}
            rules={[
              {
                required: true,
                message: t('orgRegComp.masterStudentsNeeded'),
              },
              {
                type: 'number',
                min: 0,
                message: t('orgRegComp.nonNegativeNumber'),
              },
            ]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder={t('orgRegComp.masterStudents')}
            />
          </Form.Item>

          <Form.Item label={null}>
            <Button type="primary" htmlType="submit">
              {t('gradingComp.submit')}
            </Button>
          </Form.Item>
        </Col>
      </Form>
    </div>
  );
};
